import React, { useEffect, useState } from 'react';
import { useLanguage } from './LanguageContext';
import { procedures } from './pages/content/ProceduresData';
import { surgeries } from './pages/content/SurgeriesData';
import { getWhatsAppMessage, WHATSAPP_PHONE } from './utils/whatsapp';
import './MobileConsultationForm.css';

const formCopy = {
  EN: {
    eyebrow: 'PRIVATE INTAKE',
    title: 'Request Your Evaluation',
    subtitle: 'Share a few details and we will prepare your WhatsApp message for Dr. Juan Salazar and his team.',
    nameLabel: 'Full Name',
    namePlaceholder: 'Your full name',
    ageLabel: 'Age',
    agePlaceholder: 'Age',
    countryLabel: 'Country',
    countryPlaceholder: 'Country of residence',
    interestLabel: 'Interested In',
    proceduresLabel: 'Procedures',
    surgeriesLabel: 'Surgeries',
    selectionLabel: 'Areas of Interest',
    selectProcedures: 'Select procedures',
    selectSurgeries: 'Select surgeries',
    budgetLabel: 'Estimated Budget',
    proceduresBudgetLabel: 'Procedures Budget',
    surgeriesBudgetLabel: 'Surgeries Budget',
    selectBudget: 'Select budget',
    selectedProceduresLabel: 'Selected Procedures',
    selectedSurgeriesLabel: 'Selected Surgeries',
    chooseProceduresTitle: 'Choose Your Procedures',
    chooseSurgeriesTitle: 'Choose Your Surgeries',
    chooseTreatmentsSubtitle: 'Select one or more options and save your selection.',
    chooseBudgetTitle: 'Select Budget',
    proceduresBudgetHint: 'Estimated range in USD for procedures.',
    surgeriesBudgetHint: 'Estimated range in USD for surgeries.',
    saveSelection: 'Save Selection',
    saveBudget: 'Save Budget',
    closeSheetLabel: 'Close',
    submitLabel: 'Request Evaluation',
    note: 'We will open WhatsApp with your request and all the information below.',
  },
  ES: {
    eyebrow: 'VALORACIÓN PRIVADA',
    title: 'Solicita Tu Valoración',
    subtitle: 'Comparte algunos datos y prepararemos tu mensaje de WhatsApp para el Dr. Juan Salazar y su equipo.',
    nameLabel: 'Nombre Completo',
    namePlaceholder: 'Tu nombre completo',
    ageLabel: 'Edad',
    agePlaceholder: 'Edad',
    countryLabel: 'País',
    countryPlaceholder: 'País de residencia',
    interestLabel: 'Interés Principal',
    proceduresLabel: 'Procedimientos',
    surgeriesLabel: 'Cirugías',
    selectionLabel: 'Tratamientos de Interés',
    selectProcedures: 'Selecciona procedimientos',
    selectSurgeries: 'Selecciona cirugías',
    budgetLabel: 'Presupuesto Estimado',
    proceduresBudgetLabel: 'Presupuesto Procedimientos',
    surgeriesBudgetLabel: 'Presupuesto Cirugías',
    selectBudget: 'Selecciona presupuesto',
    selectedProceduresLabel: 'Procedimientos Seleccionados',
    selectedSurgeriesLabel: 'Cirugías Seleccionadas',
    chooseProceduresTitle: 'Selecciona tus procedimientos',
    chooseSurgeriesTitle: 'Selecciona tus cirugías',
    chooseTreatmentsSubtitle: 'Puedes elegir una o varias opciones y guardar tu selección.',
    chooseBudgetTitle: 'Selecciona presupuesto',
    proceduresBudgetHint: 'Rango estimado en USD para procedimientos.',
    surgeriesBudgetHint: 'Rango estimado en USD para cirugías.',
    saveSelection: 'Guardar selección',
    saveBudget: 'Guardar presupuesto',
    closeSheetLabel: 'Cerrar',
    submitLabel: 'Solicitar Valoración',
    note: 'Abriremos WhatsApp con tu solicitud y toda la información diligenciada.',
  },
  PT: {
    eyebrow: 'AVALIAÇÃO PRIVADA',
    title: 'Solicite Sua Avaliação',
    subtitle: 'Compartilhe alguns dados e prepararemos sua mensagem de WhatsApp para o Dr. Juan Salazar e sua equipe.',
    nameLabel: 'Nome Completo',
    namePlaceholder: 'Seu nome completo',
    ageLabel: 'Idade',
    agePlaceholder: 'Idade',
    countryLabel: 'País',
    countryPlaceholder: 'País de residência',
    interestLabel: 'Interesse Principal',
    proceduresLabel: 'Procedimentos',
    surgeriesLabel: 'Cirurgias',
    selectionLabel: 'Tratamentos de Interesse',
    selectProcedures: 'Selecione procedimentos',
    selectSurgeries: 'Selecione cirurgias',
    budgetLabel: 'Orçamento Estimado',
    proceduresBudgetLabel: 'Orçamento Procedimentos',
    surgeriesBudgetLabel: 'Orçamento Cirurgias',
    selectBudget: 'Selecione orçamento',
    selectedProceduresLabel: 'Procedimentos Selecionados',
    selectedSurgeriesLabel: 'Cirurgias Selecionadas',
    chooseProceduresTitle: 'Selecione seus procedimentos',
    chooseSurgeriesTitle: 'Selecione suas cirurgias',
    chooseTreatmentsSubtitle: 'Você pode escolher uma ou várias opções e salvar sua seleção.',
    chooseBudgetTitle: 'Selecione orçamento',
    proceduresBudgetHint: 'Faixa estimada em USD para procedimentos.',
    surgeriesBudgetHint: 'Faixa estimada em USD para cirurgias.',
    saveSelection: 'Salvar seleção',
    saveBudget: 'Salvar orçamento',
    closeSheetLabel: 'Fechar',
    submitLabel: 'Solicitar Avaliação',
    note: 'Abriremos o WhatsApp com sua solicitação e todos os dados preenchidos.',
  },
};

const budgetOptions = {
  procedures: {
    EN: [
      { value: 'under-500', label: 'Under USD 500' },
      { value: '500-1200', label: 'USD 500 - 1,200' },
      { value: '1200-2500', label: 'USD 1,200 - 2,500' },
      { value: 'above-2500', label: 'Above USD 2,500' },
    ],
    ES: [
      { value: 'under-500', label: 'Menos de USD 500' },
      { value: '500-1200', label: 'USD 500 - 1.200' },
      { value: '1200-2500', label: 'USD 1.200 - 2.500' },
      { value: 'above-2500', label: 'Más de USD 2.500' },
    ],
    PT: [
      { value: 'under-500', label: 'Menos de USD 500' },
      { value: '500-1200', label: 'USD 500 - 1.200' },
      { value: '1200-2500', label: 'USD 1.200 - 2.500' },
      { value: 'above-2500', label: 'Acima de USD 2.500' },
    ],
  },
  surgeries: {
    EN: [
      { value: '3000-5000', label: 'USD 3,000 - 5,000' },
      { value: '5000-8000', label: 'USD 5,000 - 8,000' },
      { value: '8000-12000', label: 'USD 8,000 - 12,000' },
      { value: 'above-12000', label: 'Above USD 12,000' },
    ],
    ES: [
      { value: '3000-5000', label: 'USD 3.000 - 5.000' },
      { value: '5000-8000', label: 'USD 5.000 - 8.000' },
      { value: '8000-12000', label: 'USD 8.000 - 12.000' },
      { value: 'above-12000', label: 'Más de USD 12.000' },
    ],
    PT: [
      { value: '3000-5000', label: 'USD 3.000 - 5.000' },
      { value: '5000-8000', label: 'USD 5.000 - 8.000' },
      { value: '8000-12000', label: 'USD 8.000 - 12.000' },
      { value: 'above-12000', label: 'Acima de USD 12.000' },
    ],
  },
};

function MobileConsultationForm() {
  const { language } = useLanguage();
  const copy = formCopy[language] || formCopy.EN;

  const [fullName, setFullName] = useState('');
  const [age, setAge] = useState('');
  const [country, setCountry] = useState('');
  const [interestType, setInterestType] = useState('procedures');
  const [selectedProcedures, setSelectedProcedures] = useState([]);
  const [selectedSurgeries, setSelectedSurgeries] = useState([]);
  const [proceduresBudget, setProceduresBudget] = useState('');
  const [surgeriesBudget, setSurgeriesBudget] = useState('');
  const [activeSheet, setActiveSheet] = useState(null);

  const currentItems = interestType === 'procedures' ? procedures : surgeries;
  const currentSelections = interestType === 'procedures' ? selectedProcedures : selectedSurgeries;
  const currentBudgetOptions = budgetOptions[interestType][language] || budgetOptions[interestType].EN;
  const currentBudgetValue = interestType === 'procedures' ? proceduresBudget : surgeriesBudget;

  const selectedProcedureLabels = procedures
    .filter((item) => selectedProcedures.includes(item.id))
    .map((item) => item.name[language] || item.name.EN);

  const selectedSurgeryLabels = surgeries
    .filter((item) => selectedSurgeries.includes(item.id))
    .map((item) => item.name[language] || item.name.EN);

  const proceduresBudgetLabel = (budgetOptions.procedures[language] || budgetOptions.procedures.EN)
    .find((option) => option.value === proceduresBudget)?.label || '';
  const surgeriesBudgetLabel = (budgetOptions.surgeries[language] || budgetOptions.surgeries.EN)
    .find((option) => option.value === surgeriesBudget)?.label || '';

  const currentTriggerText = (() => {
    if (interestType === 'procedures') {
      if (selectedProcedureLabels.length === 0) {
        return copy.selectProcedures;
      }

      return selectedProcedureLabels.length === 1
        ? selectedProcedureLabels[0]
        : `${selectedProcedureLabels[0]} +${selectedProcedureLabels.length - 1}`;
    }

    if (selectedSurgeryLabels.length === 0) {
      return copy.selectSurgeries;
    }

    return selectedSurgeryLabels.length === 1
      ? selectedSurgeryLabels[0]
      : `${selectedSurgeryLabels[0]} +${selectedSurgeryLabels.length - 1}`;
  })();

  const treatmentSheetTitle = interestType === 'procedures' ? copy.chooseProceduresTitle : copy.chooseSurgeriesTitle;
  const treatmentSheetSubtitle = copy.chooseTreatmentsSubtitle;
  const budgetSheetSubtitle = interestType === 'procedures' ? copy.proceduresBudgetHint : copy.surgeriesBudgetHint;
  const selectedInterestLabel = interestType === 'procedures' ? copy.proceduresLabel : copy.surgeriesLabel;

  const isFormComplete = (
    fullName.trim() &&
    age.trim() &&
    country.trim() &&
    (selectedProcedures.length > 0 || selectedSurgeries.length > 0) &&
    (selectedProcedures.length === 0 || proceduresBudget) &&
    (selectedSurgeries.length === 0 || surgeriesBudget)
  );

  useEffect(() => {
    if (!activeSheet) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [activeSheet]);

  const closeSheet = () => {
    setActiveSheet(null);
  };

  const removeSelectedItem = (type, itemId) => {
    if (type === 'procedures') {
      setSelectedProcedures((current) => {
        const next = current.filter((currentId) => currentId !== itemId);

        if (next.length === 0) {
          setProceduresBudget('');
        }

        return next;
      });

      return;
    }

    setSelectedSurgeries((current) => {
      const next = current.filter((currentId) => currentId !== itemId);

      if (next.length === 0) {
        setSurgeriesBudget('');
      }

      return next;
    });
  };

  const handleItemToggle = (itemId) => {
    if (interestType === 'procedures') {
      setSelectedProcedures((current) => (
        current.includes(itemId)
          ? current.filter((currentId) => currentId !== itemId)
          : [...current, itemId]
      ));
      return;
    }

    setSelectedSurgeries((current) => (
      current.includes(itemId)
        ? current.filter((currentId) => currentId !== itemId)
        : [...current, itemId]
    ));
  };

  const handleBudgetSelect = (value) => {
    if (interestType === 'procedures') {
      setProceduresBudget(value);
      return;
    }

    setSurgeriesBudget(value);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!isFormComplete) {
      return;
    }

    const whatsappLines = [
      getWhatsAppMessage(language),
      '',
      `${copy.nameLabel}: ${fullName.trim()}`,
      `${copy.ageLabel}: ${age.trim()}`,
      `${copy.countryLabel}: ${country.trim()}`,
    ];

    if (selectedProcedureLabels.length > 0) {
      whatsappLines.push(`${copy.proceduresLabel}: ${selectedProcedureLabels.join(', ')}`);
    }

    if (proceduresBudgetLabel) {
      whatsappLines.push(`${copy.proceduresBudgetLabel}: ${proceduresBudgetLabel}`);
    }

    if (selectedSurgeryLabels.length > 0) {
      whatsappLines.push(`${copy.surgeriesLabel}: ${selectedSurgeryLabels.join(', ')}`);
    }

    if (surgeriesBudgetLabel) {
      whatsappLines.push(`${copy.surgeriesBudgetLabel}: ${surgeriesBudgetLabel}`);
    }

    window.location.href = `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(whatsappLines.join('\n'))}`;
  };

  return (
    <section className="mobile-consultation-card">
      <div className="mobile-consultation-card__header">
        <p className="mobile-consultation-card__eyebrow">{copy.eyebrow}</p>
        <h2 className="mobile-consultation-card__title">{copy.title}</h2>
        <p className="mobile-consultation-card__subtitle">{copy.subtitle}</p>
      </div>

      <form className="mobile-consultation-form" onSubmit={handleSubmit}>
        <label className="mobile-consultation-field mobile-consultation-field--full">
          <span className="mobile-consultation-label">{copy.nameLabel}</span>
          <input
            type="text"
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            placeholder={copy.namePlaceholder}
            autoComplete="name"
          />
        </label>

        <div className="mobile-consultation-grid">
          <label className="mobile-consultation-field">
            <span className="mobile-consultation-label">{copy.ageLabel}</span>
            <input
              type="number"
              min="0"
              value={age}
              onChange={(event) => setAge(event.target.value)}
              placeholder={copy.agePlaceholder}
              inputMode="numeric"
            />
          </label>

          <label className="mobile-consultation-field">
            <span className="mobile-consultation-label">{copy.countryLabel}</span>
            <input
              type="text"
              value={country}
              onChange={(event) => setCountry(event.target.value)}
              placeholder={copy.countryPlaceholder}
              autoComplete="country-name"
            />
          </label>
        </div>

        <div className="mobile-consultation-field">
          <span className="mobile-consultation-label">{copy.interestLabel}</span>
          <div className="mobile-consultation-toggle">
            <button
              type="button"
              className={interestType === 'procedures' ? 'is-active' : ''}
              onClick={() => setInterestType('procedures')}
            >
              {copy.proceduresLabel}
            </button>
            <button
              type="button"
              className={interestType === 'surgeries' ? 'is-active' : ''}
              onClick={() => setInterestType('surgeries')}
            >
              {copy.surgeriesLabel}
            </button>
          </div>
        </div>

        <div className="mobile-consultation-field">
          <span className="mobile-consultation-label">{copy.selectionLabel}</span>
          <button
            type="button"
            className="mobile-consultation-picker"
            onClick={() => setActiveSheet('treatments')}
            aria-haspopup="dialog"
            aria-expanded={activeSheet === 'treatments'}
          >
            <span className={`mobile-consultation-picker__text ${currentSelections.length > 0 ? 'is-selected' : ''}`}>
              {currentTriggerText}
            </span>
            <span className="mobile-consultation-picker__meta">
              {currentSelections.length > 0 && (
                <span className="mobile-consultation-picker__count">{currentSelections.length}</span>
              )}
              <span className="mobile-consultation-picker__arrow" />
            </span>
          </button>
        </div>

        {(selectedProcedureLabels.length > 0 || selectedSurgeryLabels.length > 0) && (
          <div className="mobile-consultation-selection-groups">
            {selectedProcedureLabels.length > 0 && (
              <div className="mobile-consultation-selection-group">
                <p className="mobile-consultation-selection-title">{copy.selectedProceduresLabel}</p>
                <div className="mobile-consultation-chip-list">
                  {selectedProcedures.map((procedureId) => {
                    const procedure = procedures.find((item) => item.id === procedureId);

                    if (!procedure) {
                      return null;
                    }

                    const label = procedure.name[language] || procedure.name.EN;

                    return (
                      <span key={`procedure-${procedureId}`} className="mobile-consultation-chip">
                        <span className="mobile-consultation-chip__label">{label}</span>
                        <button
                          type="button"
                          className="mobile-consultation-chip__remove"
                          onClick={() => removeSelectedItem('procedures', procedureId)}
                          aria-label={`Remove ${label}`}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}

            {selectedSurgeryLabels.length > 0 && (
              <div className="mobile-consultation-selection-group">
                <p className="mobile-consultation-selection-title">{copy.selectedSurgeriesLabel}</p>
                <div className="mobile-consultation-chip-list">
                  {selectedSurgeries.map((surgeryId) => {
                    const surgery = surgeries.find((item) => item.id === surgeryId);

                    if (!surgery) {
                      return null;
                    }

                    const label = surgery.name[language] || surgery.name.EN;

                    return (
                      <span key={`surgery-${surgeryId}`} className="mobile-consultation-chip">
                        <span className="mobile-consultation-chip__label">{label}</span>
                        <button
                          type="button"
                          className="mobile-consultation-chip__remove"
                          onClick={() => removeSelectedItem('surgeries', surgeryId)}
                          aria-label={`Remove ${label}`}
                        >
                          ×
                        </button>
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {selectedProcedureLabels.length > 0 && (
          <div className="mobile-consultation-field">
            <span className="mobile-consultation-label">{copy.proceduresBudgetLabel}</span>
            <button
              type="button"
              className="mobile-consultation-picker"
              onClick={() => {
                setInterestType('procedures');
                setActiveSheet('budget');
              }}
              aria-haspopup="dialog"
              aria-expanded={activeSheet === 'budget' && interestType === 'procedures'}
            >
              <span className={`mobile-consultation-picker__text ${proceduresBudget ? 'is-selected' : ''}`}>
                {proceduresBudgetLabel || copy.selectBudget}
              </span>
              <span className="mobile-consultation-picker__meta">
                <span className="mobile-consultation-picker__arrow" />
              </span>
            </button>
          </div>
        )}

        {selectedSurgeryLabels.length > 0 && (
          <div className="mobile-consultation-field">
            <span className="mobile-consultation-label">{copy.surgeriesBudgetLabel}</span>
            <button
              type="button"
              className="mobile-consultation-picker"
              onClick={() => {
                setInterestType('surgeries');
                setActiveSheet('budget');
              }}
              aria-haspopup="dialog"
              aria-expanded={activeSheet === 'budget' && interestType === 'surgeries'}
            >
              <span className={`mobile-consultation-picker__text ${surgeriesBudget ? 'is-selected' : ''}`}>
                {surgeriesBudgetLabel || copy.selectBudget}
              </span>
              <span className="mobile-consultation-picker__meta">
                <span className="mobile-consultation-picker__arrow" />
              </span>
            </button>
          </div>
        )}

        <button
          type="submit"
          className="mobile-consultation-submit"
          disabled={!isFormComplete}
        >
          {copy.submitLabel}
        </button>

        <p className="mobile-consultation-note">{copy.note}</p>
      </form>

      {activeSheet && (
        <div
          className="mobile-consultation-sheet-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label={activeSheet === 'treatments' ? treatmentSheetTitle : copy.chooseBudgetTitle}
          onClick={closeSheet}
        >
          <div
            className="mobile-consultation-sheet"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mobile-consultation-sheet__handle" />

            <div className="mobile-consultation-sheet__header">
              <div className="mobile-consultation-sheet__heading">
                <p className="mobile-consultation-sheet__eyebrow">
                  {activeSheet === 'treatments' ? selectedInterestLabel : copy.budgetLabel}
                </p>
                <h3 className="mobile-consultation-sheet__title">
                  {activeSheet === 'treatments' ? treatmentSheetTitle : copy.chooseBudgetTitle}
                </h3>
                <p className="mobile-consultation-sheet__subtitle">
                  {activeSheet === 'treatments' ? treatmentSheetSubtitle : budgetSheetSubtitle}
                </p>
              </div>

              <button
                type="button"
                className="mobile-consultation-sheet__close"
                onClick={closeSheet}
                aria-label={copy.closeSheetLabel}
              >
                ×
              </button>
            </div>

            <div className="mobile-consultation-sheet__scroll">
              {activeSheet === 'treatments' ? (
                currentItems.map((item) => {
                  const isSelected = currentSelections.includes(item.id);
                  const label = item.name[language] || item.name.EN;

                  return (
                    <button
                      type="button"
                      key={item.id}
                      className={`mobile-consultation-option ${isSelected ? 'is-selected' : ''}`}
                      onClick={() => handleItemToggle(item.id)}
                    >
                      <span className="mobile-consultation-option__title">{label}</span>
                      <span
                        className={`mobile-consultation-option__indicator ${isSelected ? 'is-selected' : ''}`}
                      />
                    </button>
                  );
                })
              ) : (
                currentBudgetOptions.map((option) => {
                  const isSelected = currentBudgetValue === option.value;

                  return (
                    <button
                      type="button"
                      key={option.value}
                      className={`mobile-consultation-option ${isSelected ? 'is-selected' : ''}`}
                      onClick={() => handleBudgetSelect(option.value)}
                    >
                      <span className="mobile-consultation-option__title">{option.label}</span>
                      <span
                        className={`mobile-consultation-option__indicator ${isSelected ? 'is-selected' : ''}`}
                      />
                    </button>
                  );
                })
              )}
            </div>

            <div className="mobile-consultation-sheet__footer">
              <button
                type="button"
                className="mobile-consultation-sheet__confirm"
                onClick={closeSheet}
              >
                {activeSheet === 'treatments' ? copy.saveSelection : copy.saveBudget}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default MobileConsultationForm;
